import { ReplaySubject, Subject } from 'rxjs';
import { ResourceService } from 'services/resource-service';
import { ToolsService } from 'services/tools-service';
import { WhiteLabelService } from 'services/white-label-service';
import { SMART_ASSISTANT_CREATION_NOTIF_ID } from 'shared/constants/notification-ids';
import {
  DEFAULT_SMART_ASSISTANT_SIZE,
  GenerateBehaviorPromptDto,
  SMART_ASSISTANT_TIMEOUT
} from 'shared/constants/smart-assistant';
import {
  AskAboutResourceDto,
  AskSmartAssistantDto,
  ChatAttachment,
  SmartAssistantMessageEvent,
  SmartAssistantSyncMessageResponse,
  TranscribedAudioAttachment
} from 'shared/interfaces/ChatMessage';
import { Resource, UnUploadedResource } from 'shared/interfaces/Resource';
import { Size } from 'shared/interfaces/Size';
import {
  CreateSmartAssistantDto,
  SmartAssistant,
  SmartAssistantCreationProgress,
  SmartAssistantStats,
  UpdateSmartAssistantAvatarDto,
  UpdateSmartAssistantDto,
  UploadAttachmentDto
} from 'shared/interfaces/SmartAssistant';
import { axiosBaseInstance } from 'shared/utils/axios-instances';
import { handleSmartAssistantAnswer, reducePartialResponses } from 'shared/utils/smart-assistant';
import { isNullWhiteLabelIntegration } from 'shared/utils/white-label';

const smartAssistantBaseEndpoint = '/smart-assistant';
const SMART_ASSISTANT_SIZE_KEY = 'smartAssistantSize';

export const SmartAssistantService = {
  getAllSmartAssistants: async (): Promise<SmartAssistant[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/all`
    });

    const smartAssistants = response.data as SmartAssistant[];
    return smartAssistants.sort((a, b) => b.createdAt - a.createdAt);
  },

  getAllSmartAssistantsCreatedByMe: async (): Promise<SmartAssistant[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/created-by-me`
    });

    const smartAssistants = response.data as SmartAssistant[];
    return smartAssistants.sort((a, b) => b.createdAt - a.createdAt);
  },

  getSmartAssistantByWhatsAppPhoneNumberId: async (
    whatsAppPhoneNumberId: string
  ): Promise<SmartAssistant | undefined> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/whatsapp-phone-number/${whatsAppPhoneNumberId}`
    });

    return response.data;
  },

  getSmartAssistantsByWhatsAppAccountId: async (whatsAppAccountId: string): Promise<SmartAssistant | undefined> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/whatsapp-account-id/${whatsAppAccountId}`
    });

    return response.data;
  },

  getSmartAssistantsByFacebookAccountId: async (facebookAccountId: string): Promise<SmartAssistant | undefined> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/facebook-account-id/${facebookAccountId}`
    });

    return response.data;
  },

  getSmartAssistantsByInstagramAccountId: async (instagramAccountId: string): Promise<SmartAssistant | undefined> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/instagram-account-id/${instagramAccountId}`
    });

    return response.data;
  },

  getAllSmartAssistantsByTeam: async (teamId: string): Promise<SmartAssistant[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/team/${teamId}`
    });

    const smartAssistants = response.data as SmartAssistant[];
    return smartAssistants.sort((a, b) => b.createdAt - a.createdAt);
  },

  getSmartAssistantById: async (smartAssistantId: string): Promise<SmartAssistant> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/${smartAssistantId}`
    });

    return response.data;
  },

  getSmartAssistantStatsById: async (smartAssistantId: string): Promise<SmartAssistantStats> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/stats/${smartAssistantId}`
    });

    return response.data;
  },

  getSmartAssistantResources: async (smartAssistantId: string): Promise<Resource[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${smartAssistantBaseEndpoint}/resources/${smartAssistantId}`
    });

    return response.data;
  },

  deleteSmartAssistant: async (smartAssistantId: string): Promise<string> => {
    const response = await axiosBaseInstance({
      method: 'delete',
      url: `${smartAssistantBaseEndpoint}/${smartAssistantId}`
    });

    return response.data._id;
  },

  updateSmartAssistant: async (updateSmartAssistantDto: UpdateSmartAssistantDto): Promise<SmartAssistant> => {
    const response = await axiosBaseInstance({
      method: 'put',
      url: `${smartAssistantBaseEndpoint}/${updateSmartAssistantDto.id}`,
      data: updateSmartAssistantDto
    });

    let updatedSmartAssistant = response.data as SmartAssistant;

    if (!updateSmartAssistantDto.whiteLabelIntegration) return updatedSmartAssistant;

    updatedSmartAssistant = await WhiteLabelService.updateWhiteLabelDomainAndSmartAssistantAssociation({
      smartAssistantId: updateSmartAssistantDto.id,
      whiteLabelIntegration: isNullWhiteLabelIntegration(updateSmartAssistantDto.whiteLabelIntegration)
        ? undefined
        : updateSmartAssistantDto.whiteLabelIntegration
    });

    return updatedSmartAssistant;
  },

  updateSmartAssistantAvatar: async (
    updateSmartAssistantAvatarDto: UpdateSmartAssistantAvatarDto
  ): Promise<SmartAssistant> => {
    const fileData = new FormData();
    fileData.append('file', updateSmartAssistantAvatarDto.avatarFile);

    const response = await axiosBaseInstance({
      method: 'put',
      url: `${smartAssistantBaseEndpoint}/update-avatar/${updateSmartAssistantAvatarDto.id}`,
      data: fileData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  removeSmartAssistantAvatar: async (smartAssistantId: string): Promise<SmartAssistant> => {
    const response = await axiosBaseInstance({
      method: 'delete',
      url: `${smartAssistantBaseEndpoint}/remove-avatar/${smartAssistantId}`
    });

    return response.data;
  },

  smartAssistantCreationProgress: new Subject<SmartAssistantCreationProgress>(),
  createSmartAssistant: async (createSmartAssistantDto: CreateSmartAssistantDto): Promise<SmartAssistant> => {
    const resourceIds: string[] = [];
    const resourcesToUpload: UnUploadedResource[] = [];

    for (const selectedResource of createSmartAssistantDto.selectedResources) {
      '_id' in selectedResource ? resourceIds.push(selectedResource._id) : resourcesToUpload.push(selectedResource);
    }

    if (resourcesToUpload.length > 0) {
      SmartAssistantService.smartAssistantCreationProgress.next(SmartAssistantCreationProgress.UPLOADING_RESOURCES);
      (
        await ResourceService.batchUploadResource({
          resources: resourcesToUpload,
          notificationId: SMART_ASSISTANT_CREATION_NOTIF_ID,
          notificationTitleKey: 'FEED_BACK.SMART_ASSISTANT.LOADING.UPLOADING_RESOURCES'
        })
      ).map((resource) => resourceIds.push(resource._id));
    }

    if (resourcesToUpload.length > 0)
      SmartAssistantService.smartAssistantCreationProgress.next(
        SmartAssistantCreationProgress.CREATING_SMART_ASSISTANT
      );

    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/create`,
      data: {
        type: createSmartAssistantDto.type,
        ...createSmartAssistantDto.configuration,
        ...createSmartAssistantDto.customization,
        resourceIds,
        discordIntegration: createSmartAssistantDto.discordIntegration,
        websiteIntegration: createSmartAssistantDto.websiteIntegration,
        shopifyStoreResource: createSmartAssistantDto.shopifyStoreResource,
        whatsAppIntegration: createSmartAssistantDto.whatsAppIntegration,
        voiceIntegration: createSmartAssistantDto.voiceIntegration,
        facebookIntegration: createSmartAssistantDto.facebookIntegration,
        instagramIntegration: createSmartAssistantDto.instagramIntegration,
        chatAttachmentConfiguration: createSmartAssistantDto.chatAttachmentConfiguration
      }
    });
    let createdSmartAssistant = response.data as SmartAssistant;

    if (createSmartAssistantDto.avatar) {
      SmartAssistantService.smartAssistantCreationProgress.next(SmartAssistantCreationProgress.UPLOADING_AVATAR);
      await SmartAssistantService.updateSmartAssistantAvatar({
        id: createdSmartAssistant._id,
        avatarFile: createSmartAssistantDto.avatar
      });
    }

    if (createSmartAssistantDto.toolConfigs) {
      SmartAssistantService.smartAssistantCreationProgress.next(SmartAssistantCreationProgress.CONFIGURING_TOOLS);

      await ToolsService.updateAllToolConfigs({
        smartAssistantId: createdSmartAssistant._id,
        toolConfigs: createSmartAssistantDto.toolConfigs
      });
    }

    if (
      createSmartAssistantDto.configuration.whiteLabelIntegration &&
      !isNullWhiteLabelIntegration(createSmartAssistantDto.configuration.whiteLabelIntegration)
    ) {
      createdSmartAssistant = await WhiteLabelService.updateWhiteLabelDomainAndSmartAssistantAssociation({
        smartAssistantId: createdSmartAssistant._id,
        whiteLabelIntegration: createSmartAssistantDto.configuration.whiteLabelIntegration
      });
    }

    if (resourcesToUpload.length > 0)
      SmartAssistantService.smartAssistantCreationProgress.next(SmartAssistantCreationProgress.DONE);

    return createdSmartAssistant;
  },

  askAboutResource: async (
    askAboutResourceDto: AskAboutResourceDto,
    eventSubject?: Subject<SmartAssistantMessageEvent>,
    partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>
  ): Promise<SmartAssistantMessageEvent[]> => {
    let accumulatedMessageEvents: SmartAssistantMessageEvent[] = [];
    let caughtError;

    await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/ask-about-resource`,
      onDownloadProgress(progressEvent) {
        try {
          accumulatedMessageEvents = handleSmartAssistantAnswer(
            progressEvent,
            accumulatedMessageEvents,
            eventSubject,
            partialResponseSubject
          );
        } catch (e) {
          caughtError = e;
        }
      },
      data: askAboutResourceDto,
      timeout: SMART_ASSISTANT_TIMEOUT
    });

    if (caughtError) throw caughtError;

    return reducePartialResponses(accumulatedMessageEvents);
  },

  askSmartAssistant: async (
    askSmartAssistantDto: AskSmartAssistantDto,
    eventSubject?: Subject<SmartAssistantMessageEvent>,
    partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>
  ): Promise<SmartAssistantMessageEvent[]> => {
    let accumulatedMessageEvents: SmartAssistantMessageEvent[] = [];
    let caughtError;

    await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/ask`,
      onDownloadProgress(progressEvent) {
        try {
          accumulatedMessageEvents = handleSmartAssistantAnswer(
            progressEvent,
            accumulatedMessageEvents,
            eventSubject,
            partialResponseSubject
          );
        } catch (e) {
          caughtError = e;
        }
      },
      data: askSmartAssistantDto,
      timeout: SMART_ASSISTANT_TIMEOUT
    });

    if (caughtError) throw caughtError;

    return reducePartialResponses(accumulatedMessageEvents);
  },

  askSmartAssistantSync: async (
    askSmartAssistantDto: AskSmartAssistantDto
  ): Promise<SmartAssistantSyncMessageResponse> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/ask-sync`,
      data: askSmartAssistantDto
    });

    return response.data;
  },

  saveSmartAssistantSize: (size: Size) => localStorage.setItem(SMART_ASSISTANT_SIZE_KEY, JSON.stringify(size)),

  getSmartAssistantSize: (): Size => {
    const size = localStorage.getItem(SMART_ASSISTANT_SIZE_KEY);
    return size ? JSON.parse(size) : DEFAULT_SMART_ASSISTANT_SIZE;
  },

  createTag: async (tag: string, smartAssistantIds: string[]): Promise<SmartAssistant[]> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/tag`,
      data: { tag, smartAssistantIds }
    });

    return response.data;
  },

  generateBehaviorPrompt: async (generateBehaviorPromptDto: GenerateBehaviorPromptDto): Promise<string> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/generate-behavior-prompt`,
      data: generateBehaviorPromptDto
    });

    return response.data;
  },

  togglePauseIntegration: async (smartAssistantId: string): Promise<SmartAssistant> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/toggle-pause-integration/${smartAssistantId}`
    });

    return response.data;
  },

  uploadAttachment: async (uploadAttachmentDto: UploadAttachmentDto): Promise<ChatAttachment> => {
    const fileData = new FormData();
    fileData.append('file', uploadAttachmentDto.file);
    fileData.append('smartAssistantId', uploadAttachmentDto.smartAssistantId);
    fileData.append('conversationId', uploadAttachmentDto.conversationId);
    fileData.append('type', uploadAttachmentDto.type);

    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/upload-attachment`,
      data: fileData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  transcribeAudioAttachment: async ({ file }: { file: File }): Promise<TranscribedAudioAttachment> => {
    const fileData = new FormData();
    fileData.append('file', file);

    const response = await axiosBaseInstance({
      method: 'post',
      url: `${smartAssistantBaseEndpoint}/transcribe-audio-attachment`,
      data: fileData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  }
};
